import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'
import {
  acquireRelay,
  releaseRelay,
  refreshRelaySnapshot,
  subscribeRelayRegistry,
  isRelayActive,
  getRelayStatus,
  getRelayError,
  getRelayProvisioning,
  getRelayModelFailures,
  getRelayStore,
  isRelayPassive,
  dispatchRelayTurn,
} from '@github-ui/ahp-relay/relay-registry'
import type {
  RelayStatus,
  RelayModelCallFailure,
  RelayCreateSessionOptions,
} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'
import {AhpChatStore} from '@github-ui/ahp-relay/ahp-chat-store'
import {getTurns, isWorking as selectIsWorking} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {WebSocketFactory} from '@github-ui/ahp-relay/webpubsub/wps-transport'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {mcFetcher} from '../utils/mc-fetcher'

export interface UseAhpSessionStateOptions {
  taskId: string | undefined
  environmentId: string | undefined
  /** Mission Control session id the relay observes. */
  sessionId: string | undefined
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: string
  /**
   * When set, the relay create-or-attaches its daemon session (attach if it
   * exists, create otherwise). Managed sandbox passes `{attachOrCreate: true}` so
   * the task view can re-establish a session the create page's runtime never landed.
   */
  createSession?: RelayCreateSessionOptions
  /**
   * Prompt to dispatch once, if the connected session is still empty (no turns,
   * not working). Recovers a first turn the create page never landed after a
   * hard nav; the emptiness guard keeps it from double-dispatching.
   */
  firstTurn?: string
  /** Opens the websocket used by the underlying WPS transport. */
  webSocketFactory?: WebSocketFactory
  /** Observes relay status changes for host-app diagnostics. */
  onStatus?: (status: RelayStatus, error?: string) => void
  /** Called after this hook acquires the shared relay. */
  onRelayAcquired?: (taskId: string) => void
  /** When false the relay is inert (no connection, no registry entry). */
  enabled: boolean
}

export interface UseAhpSessionStateResult {
  /** The canonical AHP chat state, or undefined before the first snapshot. */
  state: ChatState | undefined
  /** Whether the agent is actively working (an active turn is present). */
  isWorking: boolean
  active: boolean
  status: RelayStatus | 'idle'
  error?: string
  /** True when the host opened this AHP connection as observe-only. */
  passive: boolean
  /** Repo-project provisioning progress, when active. */
  provisioning?: ProvisioningStatus
  /** Model-call failures keyed by turn id; consumers decorate the matching turn as errored. */
  modelFailures?: Record<string, RelayModelCallFailure>
}

/**
 * Acquires a shared AHP relay and exposes its live `ChatState`.
 */
export function useAhpSessionState({
  taskId,
  environmentId,
  sessionId,
  channel,
  createSession,
  firstTurn,
  webSocketFactory,
  onStatus,
  onRelayAcquired,
  enabled,
}: UseAhpSessionStateOptions): UseAhpSessionStateResult {
  const shouldConnect = enabled && !!taskId && !!environmentId && !!sessionId

  useEffect(() => {
    if (!shouldConnect || !taskId || !environmentId || !sessionId) return
    // Only refresh a relay that has reached `connected`; refreshing one still
    // connecting would tear down its attempt and spawn a redundant `/connect`.
    const shouldRefresh = getRelayStatus(taskId) === 'connected'
    void acquireRelay({
      taskId,
      environmentId,
      sessionId,
      ...(channel ? {channel} : {}),
      ...(createSession ? {createSession} : {}),
      ...(webSocketFactory ? {webSocketFactory} : {}),
      ...(onStatus ? {onStatus} : {}),
      fetcher: mcFetcher,
      createSink: () => new AhpChatStore(),
    })
    // A relay another consumer already runs may be stale; pull a fresh snapshot (only when connected).
    if (shouldRefresh) {
      refreshRelaySnapshot(taskId)
    }
    onRelayAcquired?.(taskId)
    return () => releaseRelay(taskId)
  }, [
    shouldConnect,
    taskId,
    environmentId,
    sessionId,
    channel,
    createSession,
    webSocketFactory,
    onStatus,
    onRelayAcquired,
  ])

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      let current = getRelayStore(taskId)
      let storeUnsubscribe = current?.subscribe(onStoreChange)
      const registryUnsubscribe = subscribeRelayRegistry(() => {
        const next = getRelayStore(taskId)
        if (next !== current) {
          // The store instance was replaced (new acquire cycle): re-attach to it
          // and notify so the fresh snapshot is read.
          storeUnsubscribe?.()
          current = next
          storeUnsubscribe = current?.subscribe(onStoreChange)
          onStoreChange()
        }
      })
      return () => {
        registryUnsubscribe()
        storeUnsubscribe?.()
      }
    },
    [taskId],
  )

  const state = useSyncExternalStore(
    subscribe,
    () => getRelayStore(taskId)?.getSnapshot(),
    () => undefined,
  )

  const active = useSyncExternalStore(
    subscribeRelayRegistry,
    () => isRelayActive(taskId),
    () => false,
  )
  const status = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayStatus(taskId),
    () => 'idle' as const,
  )
  const error = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayError(taskId),
    () => undefined,
  )
  const passive = useSyncExternalStore(
    subscribeRelayRegistry,
    () => isRelayPassive(taskId),
    () => false,
  )
  const provisioning = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayProvisioning(taskId),
    () => undefined,
  )
  const modelFailures = useSyncExternalStore(
    subscribeRelayRegistry,
    () => getRelayModelFailures(taskId),
    () => undefined,
  )

  // One-shot recovery: if we connect to a session that never received its first
  // turn (empty and idle), dispatch it. `attach` replays any in-flight turn, so a
  // turn the create page already landed shows as turns>0 or working and is skipped.
  const firstTurnDispatchedRef = useRef(false)
  useEffect(() => {
    if (!firstTurn || !taskId || status !== 'connected' || !state) return
    if (firstTurnDispatchedRef.current) return
    if (getTurns(state).length > 0 || selectIsWorking(state)) return
    firstTurnDispatchedRef.current = true
    dispatchRelayTurn(taskId, firstTurn)
  }, [firstTurn, taskId, status, state])

  return {
    state,
    isWorking: state ? selectIsWorking(state) : false,
    active,
    status,
    error,
    passive,
    ...(provisioning ? {provisioning} : {}),
    ...(modelFailures ? {modelFailures} : {}),
  }
}
